/**
 * THE chart theme — single source of truth for every chart's colors & styling.
 * Semantics (Barak 2026-07-12): RED means decline/negative ONLY — never a
 * neutral series. The benchmark is a reference line → neutral silver.
 */

// Values mirror the :root palette (styles.css). Concrete strings (not var())
// because Recharts writes SVG attributes; oklch() is SVG-paint-safe.
export const SERIES = {
  /** Portfolio cumulative line (the client's money) — chart-2 blue. */
  portfolio: 'oklch(58% 0.12 255)',
  /** Benchmark/reference lines — neutral slate, never red. */
  benchmark: 'oklch(65% 0.02 250)',
  /** Positive values: gains, deposits, up-months (--success). */
  positive: 'oklch(72% 0.16 145)',
  /** Negative values: losses, withdrawals, down-months (--destructive). */
  negative: 'oklch(55% 0.18 25)',
  /** Asset-volume / balance series (--chart-5 teal-blue). */
  volume: 'oklch(55% 0.08 200)',
} as const;

/** Sign-aware color for bar cells (monthly returns, flows). */
export const bySign = (v: number): string => (v >= 0 ? SERIES.positive : SERIES.negative);

/** Allocation pie slices — the palette's chart-1..5 + derived tints, no semantic red. */
export const PIE_COLORS = [
  'oklch(68% 0.14 150)', // chart-1
  'oklch(58% 0.12 255)', // chart-2
  'oklch(65% 0.09 110)', // chart-3
  'oklch(72% 0.1 75)',   // chart-4
  'oklch(55% 0.08 200)', // chart-5
  'oklch(78% 0.12 150)',
  'oklch(70% 0.10 255)',
  'oklch(75% 0.08 110)',
  'oklch(80% 0.08 75)',
  'oklch(65% 0.06 200)',
  'oklch(72% 0.01 250)',
] as const;

/** Shared canvas styling (THEME.md §7). */
export const GRID = { strokeDasharray: '3 3', stroke: 'var(--border)' } as const;
export const AXIS = { stroke: 'var(--foreground)', fontSize: 11 } as const;
export const AXIS_SECONDARY = { stroke: 'var(--foreground)', fontSize: 11 } as const;

/**
 * Y-axis band widths. The tick numbers sit beside the axis line (see the
 * .recharts-yAxis rule in styles.css), so the band has to hold the widest
 * label the scale can produce — "-100%" at 11px, plus recharts' 8px tick gap —
 * and, where there is one, the rotated axis title drawn inside the same band.
 */
export const PCT_AXIS_WIDTH = 46;
export const PCT_AXIS_WIDTH_TITLED = 64;
/** Money scales print as "-1000M"; same idea, a little more room. */
export const MONEY_AXIS_WIDTH = 52;
export const MONEY_AXIS_WIDTH_TITLED = 70;
export const TOOLTIP_STYLE = {
  background: '#122640',
  border: '1px solid #1E2D3D',
  borderRadius: 8,
  direction: 'rtl',
  color: '#F0F2F5',
} as const;

/**
 * Complete themed Tooltip props — spread onto EVERY <Tooltip>:
 *   <Tooltip {...TOOLTIP_PROPS} formatter={...} />
 * Fixes recharts' dark default label/item text (unreadable on the dark canvas).
 */
export const TOOLTIP_PROPS = {
  contentStyle: TOOLTIP_STYLE,
  labelStyle: { color: '#F0F2F5', fontWeight: 600, marginBottom: 4 },
  itemStyle: { color: '#FFFFFF' },
  // Line charts draw the cursor as a vertical line (stroke); bar charts as a
  // filled rect (fill) — recharts' default #ccc rect glares on the dark theme.
  cursor: { fill: 'rgba(147, 184, 240, 0.08)', stroke: '#FFFFFF', strokeWidth: 1 },
} as const;

/** Gradient stops for the asset-volume charts. */
export const VOLUME_GRADIENT = { from: '#3B82F6', to: '#1E3A5F' } as const;

/** The axis span drawn when every value on a percentage axis is 0: ±1%. */
export const FLAT_PCT_DOMAIN: [number, number] = [-0.01, 0.01];

/**
 * The domain for a percentage axis. Left to recharts, a series of nothing but
 * zeros — an alternative investment's carried months, a month with no movement —
 * is drawn on a 0–400% axis, which reads as if something huge happened. When
 * every value is 0 (or there is none), the axis is a small band around 0
 * instead; any real value leaves recharts' own scaling alone.
 */
export function pctAxisDomain(values: (number | null | undefined)[]): [number, number] | undefined {
  return values.every((v) => v === null || v === undefined || !Number.isFinite(v) || v === 0) ? FLAT_PCT_DOMAIN : undefined;
}
